import axios, { AxiosError } from 'axios';
import {
  IAdditionalPoints,
  ICEXCheckResponse,
  ICancelOrderResponse,
  ICexOrdersResponse,
  ICexApiResponse,
  IClaimResponse,
  ICompetitionTrade,
  ICreateOrderResponse,
  ILeaderboard,
  ICompetitionWatchlistPair,
  IRedeemCodeResponse,
  IUserBoard,
} from '../interfaces/cex';

export interface ICexApi {
  chartsEndpoint: string;
  cexTradingEndpoint: string;

  fetchKLines: (
    exchange: string,
    symbol: string,
    interval: string,
    startTime: number,
    endTime: number,
    limit: number
  ) => Promise<any>;
  fetchLastKLine: (
    exchange: string,
    symbol: string,
    interval: string
  ) => Promise<any>;
  fetchHuobiKlines: (
    exchange: string,
    symbol: string,
    interval: string,
    limit: number
  ) => Promise<any>;
  fetchGateIoCandles: (params: any) => Promise<any>;
  fetchHitbtcLastCandle: (symbol: string, interval: string) => Promise<any>;
  activateAPIKey: (
    exchange: string,
    key: string,
    secret: string,
    authToken: string
  ) => Promise<ICexApiResponse<any>>;
  revokeAPIKey: (
    authToken: string,
    exchange: string
  ) => Promise<ICexApiResponse<any>>;
  checkEnabledExchanges: (
    authToken: string
  ) => Promise<ICexApiResponse<ICEXCheckResponse>>;
  createOrder: (
    authToken: string,
    exchange: string,
    pair: string,
    orderType: number,
    orderSide: number,
    price: number,
    base_amount: number,
    quote_amount: number
  ) => Promise<ICreateOrderResponse>;
  cancelOrder: (
    authToken: string,
    exchange: string,
    pair: string,
    uuid: string
  ) => Promise<ICancelOrderResponse>;
  openOrders: (
    authToken: string,
    exchange: string,
    pair: string
  ) => Promise<ICexOrdersResponse | null>;
  historyOrders: (
    authToken: string,
    exchange: string,
    pair: string
  ) => Promise<ICexOrdersResponse | null>;
  fetchLeaderboard: (
    exchange: string,
    config: any
  ) => Promise<ILeaderboard[] | null>;
  fetchCompetitionTrades: (
    exchange: string,
    authToken: string,
    offset: number,
    controller: AbortController
  ) => Promise<ICompetitionTrade[] | null>;
  fetchUserBoard: (
    exchange: string,
    network: string,
    wallet: string,
    controller: AbortController
  ) => Promise<IUserBoard | null>;
  fetchAdditionalPoints: (
    exchange: string,
    authToken: string,
    controller: AbortController
  ) => Promise<IAdditionalPoints | null>;
  claimAdditionalPoints: (
    exchange: string,
    authToken: string
  ) => Promise<IClaimResponse | string>;
  redeemCode: (
    authToken: string,
    exchange: string,
    code: string
  ) => Promise<IRedeemCodeResponse>;
  fetchCompetitionWatchlist: (
    wallet: string,
    authToken: string,
    controller?: AbortController
  ) => Promise<ICompetitionWatchlistPair[] | null>;
}

export class CexApi implements ICexApi {
  chartsEndpoint: string;
  cexTradingEndpoint: string;

  constructor(apiGateway: string) {
    if (!apiGateway) {
      apiGateway = 'https://api.kattana.trade';
    }

    this.chartsEndpoint = `${apiGateway}/cex_charts`;
    this.cexTradingEndpoint = `${apiGateway}/cex/v1`;
  }

  /**
   * get Klines from binance or hitbtc
   */
  public fetchKLines = async (
    exchange: string,
    symbol: string,
    interval: string,
    startTime: number,
    endTime: number,
    limit: number
  ) => {
    try {
      const payload = {
        symbol,
        interval,
        startTime,
        endTime,
        limit,
      };
      const { data } = await axios(
        `${this.chartsEndpoint}/${exchange}/chart/${JSON.stringify(payload)}`
      );

      return data;
    } catch (err) {
      console.warn(err);
      return [];
    }
  };

  /**
   * Get klines without starttime or end time
   */
  public fetchHuobiKlines = async (
    exchange: string,
    symbol: string,
    interval: string,
    limit: number
  ) => {
    try {
      const payload = {
        symbol,
        interval,
        limit,
      };
      const { data } = await axios(
        `${this.chartsEndpoint}/${exchange}/chart/${JSON.stringify(payload)}`
      );

      return data;
    } catch (err) {
      console.warn(err);
      return [];
    }
  };

  /**
   * gets candles depending on params (can get last one, all etc)
   */
  public fetchGateIoCandles = async (params: any) => {
    try {
      const { data } = await axios.get(
        `${this.chartsEndpoint}/gateio/chart/${JSON.stringify(params)}`
      );

      return data;
    } catch (err) {
      console.warn(err);
      return [];
    }
  };

  /**
   *
   * @param sym
   * @param interval
   * @returns
   */
  public fetchHitbtcLastCandle = async (symbol: string, interval: string) => {
    try {
      const { data } = await axios(
        `${this.chartsEndpoint}/hitbtc/candle/${JSON.stringify({
          symbol,
          interval,
        })}`
      );
      return data;
    } catch (err) {
      console.warn(err);
      return [];
    }
  };

  /**
   * Fetch last candle
   * @param exchange
   * @param symbol
   * @param interval
   * @returns
   */
  public fetchLastKLine = async (
    exchange: string,
    symbol: string,
    interval: string
  ) => {
    try {
      const payload = {
        symbol,
        interval,
      };
      const url = `${this.chartsEndpoint}/${exchange}/candle/${JSON.stringify(
        payload
      )}`;
      const { data } = await axios(url);

      return data;
    } catch (err) {
      console.warn(err);
      return [];
    }
  };

  /**
   * Activate API key
   * @param exchange
   * @param key
   * @param secret
   * @param authToken
   * @returns
   */
  public activateAPIKey = async (
    exchange: string,
    key: string,
    secret: string,
    authToken: string
  ): Promise<ICexApiResponse<any>> => {
    try {
      const config = {
        headers: { Authorization: `Bearer ${authToken}` },
      };
      const res = await axios.post(
        `${this.cexTradingEndpoint}/key`,
        {
          exchange,
          key,
          secret,
        },
        config
      );
      return {
        data: res.data,
        status: res.status,
        success: true,
      };
    } catch (err) {
      if (axios.isAxiosError(err)) {
        return {
          success: false,
          status: err.response?.status,
          data: [],
          error: err,
        };
      }

      return {
        data: null,
        success: false,
      };
    }
  };

  /**
   * Revoke API key
   * @param authToken
   * @param exchange
   * @returns
   */
  /**
   * Revoke API key
   * @param authToken
   * @param exchange
   * @returns
   */
  public revokeAPIKey = async (
    authToken: string,
    exchange: string
  ): Promise<ICexApiResponse<any>> => {
    try {
      const config = {
        headers: { Authorization: `Bearer ${authToken}` },
      };
      const res = await axios.delete(
        `${this.cexTradingEndpoint}/key/${exchange}`,
        config
      );
      return {
        success: false,
        status: res.status,
        data: res.data,
      };
    } catch (err) {
      if (axios.isAxiosError(err)) {
        return {
          success: false,
          status: err.response?.status,
          error: err,
        };
      }

      return {
        success: false,
      };
    }
  };

  /**
   * Fetch last candle
   * @param authToken
   * @returns
   */
  public checkEnabledExchanges = async (
    authToken: string
  ): Promise<ICexApiResponse<ICEXCheckResponse>> => {
    try {
      const config = {
        headers: { Authorization: `Bearer ${authToken}` },
      };
      const res = await axios.get(`${this.cexTradingEndpoint}/check`, config);
      return {
        success: true,
        status: res.status,
        data: res.data,
      };
    } catch (err) {
      if (axios.isAxiosError(err)) {
        return {
          success: false,
          status: err.response?.status,
          error: err,
        };
      }

      return {
        success: false,
      };
    }
  };

  /**
   * Create market/limit order
   * @param authToken
   * @param exchange
   * @param pair
   * @param orderType
   * @param orderSide
   * @param price
   * @param base_amount
   * @param quote_amount
   * @returns
   */
  public createOrder = async (
    authToken: string,
    exchange: string,
    pair: string,
    orderType: number,
    orderSide: number,
    price: number,
    base_amount: number,
    quote_amount: number
  ): Promise<ICreateOrderResponse> => {
    try {
      const config = {
        headers: { Authorization: `Bearer ${authToken}` },
      };
      const res = await axios.post(
        `${this.cexTradingEndpoint}/order`,
        {
          exchange,
          pair,
          type: orderType,
          side: orderSide,
          price,
          base_amount,
          quote_amount,
        },
        config
      );
      if (res.status == 200) {
        return { status: true, data: res.data };
      }
      return { status: false, message: res.data.message };
    } catch (err) {
      if (axios.isAxiosError(err)) {
        return { status: false, message: err.response?.data?.error };
      }
      return { status: false, message: 'Could not create order' };
    }
  };

  /**
   * Create limit order
   * @param authToken
   * @param exchange
   * @param pair
   * @param uuid
   * @returns
   */
  public cancelOrder = async (
    authToken: string,
    exchange: string,
    pair: string,
    uuid: string
  ): Promise<ICancelOrderResponse> => {
    try {
      const config = {
        headers: { Authorization: `Bearer ${authToken}` },
      };
      const res = await axios.delete(
        `${this.cexTradingEndpoint}/order/${exchange}/${pair}/${uuid}`,
        config
      );
      return {
        status: res.data.status,
      };
    } catch (err) {
      console.warn(err);
      return { status: false, error: String(err) };
    }
  };

  /**
   * Fetch open limit orders
   * @param authToken
   * @param exchange
   * @param pair
   * @returns
   */
  public openOrders = async (
    authToken: string,
    exchange: string,
    pair: string
  ): Promise<ICexOrdersResponse | null> => {
    try {
      const config = {
        headers: { Authorization: `Bearer ${authToken}` },
      };
      const res = await axios.get(
        `${this.cexTradingEndpoint}/orders/${exchange}/${pair}/open`,
        config
      );
      return res.data;
    } catch (err) {
      console.warn(err);
      return null;
    }
  };

  /**
   * Fetch history orders
   * @param authToken
   * @param exchange
   * @param pair
   * @returns
   */
  public historyOrders = async (
    authToken: string,
    exchange: string,
    pair: string
  ): Promise<ICexOrdersResponse | null> => {
    try {
      const config = {
        headers: { Authorization: `Bearer ${authToken}` },
      };
      const res = await axios.get(
        `${this.cexTradingEndpoint}/orders/${exchange}/${pair}/history`,
        config
      );
      return res.data;
    } catch (err) {
      console.warn(err);
      return null;
    }
  };

  /**
   * Fetch leaderboard
   * @param exchange
   * @param config
   * @returns
   */
  public fetchLeaderboard = async (
    exchange: string,
    config: any
  ): Promise<ILeaderboard[] | null> => {
    try {
      const res = await axios.get(
        `${this.cexTradingEndpoint}/leaderboard/${exchange}`,
        config
      );
      // Return empty array otherwise page will not load
      return res.data.Leaderboard ? res.data.Leaderboard : [];
    } catch (err) {
      console.warn(err);
      return [];
    }
  };

  /**
   * Fetch competition trades
   * @param exchange
   * @param authToken
   * @param offset
   * @param controller
   * @returns
   */
  public fetchCompetitionTrades = async (
    exchange: string,
    authToken: string,
    offset: number,
    controller: AbortController
  ): Promise<ICompetitionTrade[] | null> => {
    try {
      const config = {
        headers: { Authorization: `Bearer ${authToken}` },
        signal: controller.signal,
      };
      const res = await axios.get(
        `${this.cexTradingEndpoint}/competition/${exchange}?limit=200&offset=${offset}`,
        config
      );

      return res.data.Order;
    } catch (err) {
      console.warn(err);
      return null;
    }
  };

  /**
   * Fetch user board
   * @param exchange
   * @param network
   * @param wallet
   * @param controller
   * @returns
   */
  public fetchUserBoard = async (
    exchange: string,
    network: string,
    wallet: string,
    controller: AbortController
  ): Promise<IUserBoard | null> => {
    try {
      const config = { signal: controller.signal };
      const res = await axios.get(
        `${this.cexTradingEndpoint}/user_board/${exchange}/${network}/${wallet}`,
        config
      );

      return res.data;
    } catch (err) {
      console.warn(err);
      return null;
    }
  };

  /**
   * Fetch additional points info
   * @param exchange
   * @param authToken
   * @param controller
   * @returns
   */
  public fetchAdditionalPoints = async (
    exchange: string,
    authToken: string,
    controller: AbortController
  ): Promise<IAdditionalPoints | null> => {
    try {
      const config = {
        headers: { Authorization: `Bearer ${authToken}` },
        signal: controller.signal,
      };
      const res = await axios.get(
        `${this.cexTradingEndpoint}/additional_points/${exchange}`,
        config
      );

      return res.data;
    } catch (err) {
      console.warn(err);
      return null;
    }
  };

  /**
   * Claim additional points
   * @param exchange
   * @param authToken
   * @returns
   */
  public claimAdditionalPoints = async (
    exchange: string,
    authToken: string
  ): Promise<IClaimResponse | string> => {
    try {
      const config = {
        headers: { Authorization: `Bearer ${authToken}` },
      };
      const res = await axios.get(
        `${this.cexTradingEndpoint}/claim/${exchange}`,
        config
      );

      if (res.data.claimedPoints) {
        return res.data;
      }

      return "Couldn't find any points to claim";
    } catch (err: any) {
      console.warn(err);
      return err.message;
    }
  };

  /**
   * Redeem code
   * @param authToken
   * @param exchange
   * @param code
   * @returns
   */
  public redeemCode = async (
    authToken: string,
    exchange: string,
    code: string
  ): Promise<IRedeemCodeResponse> => {
    try {
      const config = {
        headers: { Authorization: `Bearer ${authToken}` },
      };
      const res = await axios.get(
        `${this.cexTradingEndpoint}/claim_code/${exchange}?code=${code}`,
        config
      );
      const { data } = res;
      const { claimedPoints, message } = data;
      const status = claimedPoints > 0;
      return { status, message, claimedPoints };
    } catch (err: any) {
      console.warn(err);
      return { status: false };
    }
  };

  /**
   * Fetch competition watchlist
   * @param wallet
   * @param authToken
   * @param controller
   * @returns
   */
  public fetchCompetitionWatchlist = async (
    wallet: string,
    authToken: string,
    controller?: AbortController
  ): Promise<ICompetitionWatchlistPair[] | null> => {
    try {
      const config = {
        headers: {
          Authorization: `Bearer ${authToken}`,
        },
        signal: controller?.signal,
      };
      const res = await axios.get(
        `${this.cexTradingEndpoint}/watchlist/${wallet}`,
        config
      );
      if (res.data.list) {
        res.data.list.chain = '';
      }
      return res.data.list;
    } catch (err: any) {
      console.warn(err);
      return null;
    }
  };
}
